import React, { useEffect, useState } from 'react';
import { Col, DatePicker, Divider, List, Popover, Row, Select, Space, Spin, Statistic, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  AiChatHistoryConversationUserInterface,
  AiChatStatisticsInterface,
} from '../../../../../common/interfaces/aiChatSettings';
import {
  AiChatHistoryService,
  DisplayDateFormat,
  getPeriodEndDate,
  getPeriodLabel,
  getPeriodStartDate,
  JavaFormat,
  PeriodEnum,
  Periods,
} from '../../../../../common';
import moment from 'moment';
import localeHR from 'antd/es/date-picker/locale/hr_HR';

const { RangePicker } = DatePicker;

function AiChatStatisticsComponent() {
  const [period, setPeriod] = useState('1');
  const [dateRange, setDateRange] = useState([
    moment().startOf('day').format(JavaFormat),
    moment().endOf('day').format(JavaFormat),
  ]);

  const [users, setUsers] = useState<AiChatHistoryConversationUserInterface[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);

  const [statistics, setStatistics] = useState<AiChatStatisticsInterface | null>(null);

  useEffect(() => {
    AiChatHistoryService.getConversationsUsers().subscribe((response) => {
      setUsers(response);
    });
  }, []);

  useEffect(() => {
    setStatistics(null);
    AiChatHistoryService.getStatistics(dateRange[0], dateRange[1], selectedUser ?? undefined).subscribe((response) => {
      setStatistics(response);
    });
  }, [dateRange, selectedUser]);

  const onPeriodChange = (value: string) => {
    setDateRange([getPeriodStartDate(parseInt(value)), getPeriodEndDate(parseInt(value))]);
    setPeriod(value);
  };

  const onDateRangeChange = (values: any, stringValues: Array<string>) => {
    setDateRange([
      moment(stringValues[0], DisplayDateFormat).startOf('day').format(JavaFormat),
      moment(stringValues[1], DisplayDateFormat).endOf('day').format(JavaFormat),
    ]);
  };

  const renderTitle = (title: string, explanation: string): React.ReactNode => {
    return (
      <Popover content={<div>{explanation}</div>}>
        {title} &nbsp;
        <QuestionCircleOutlined />
      </Popover>
    );
  };

  const renderStatisticsTop = () => {
    const stats = [
      [
        {
          key: 'totalUsers',
          title: 'Ukupan broj korisnika',
          explanation: 'Broj korisnika koji su koristili AI chat u odabranom razdoblju.',
          value: statistics?.totalUsers,
        },
        {
          key: 'totalConversations',
          title: 'Ukupan broj razgovora',
          explanation: 'Broj razgovora između korisnika i AI chata u odabranom razdoblju.',
          value: statistics?.totalConversations,
        },
        {
          key: 'totalMessages',
          title: 'Ukupan broj poruka',
          explanation: 'Ukupan broj poruka koje su korisnici i AI chat razmijenili u odabranom razdoblju.',
          value: statistics?.totalMessages,
        },
      ],
      [
        {
          key: 'userMessagesCount',
          title: 'Poruke korisnika',
          explanation: 'Broj poruka koje su korisnici poslali AI chatu u odabranom razdoblju.',
          value: statistics?.userMessagesCount,
        },
        {
          key: 'assistantMessagesCount',
          title: 'Poruke asistenta',
          explanation: 'Broj odgovora koje je AI asistent poslao korisnicima u odabranom razdoblju.',
          value: statistics?.assistantMessagesCount,
        },
        {
          key: 'errorMessagesCount',
          title: 'Poruke s greškom',
          explanation: 'Broj poruka kod kojih je došlo do greške tijekom AI odgovora u odabranom razdoblju.',
          value: statistics?.errorMessagesCount,
        },
        {
          key: 'noResponseMessagesCount',
          title: 'Poruke bez odgovora',
          explanation: 'Broj poruka za koje AI chat nije generirao odgovor u odabranom razdoblju.',
          value: statistics?.noResponseMessagesCount,
        },
      ],
      [
        {
          key: 'avgMessagesPerConversation',
          title: 'Prosječan broj poruka po razgovoru',
          explanation: 'Prosječan broj poruka u jednom razgovoru između korisnika i AI chata u odabranom razdoblju.',
          value: statistics?.avgMessagesPerConversation,
        },
        {
          key: 'avgMessagesPerUser',
          title: 'Prosječan broj poruka po korisniku',
          explanation: 'Prosječan broj poruka razmijenjenih između jednog korisnika i AI chata u odabranom razdoblju.',
          value: statistics?.avgMessagesPerUser,
        },
      ],
    ];
    return stats.map((row, index) => (
      <Row gutter={16} key={index} style={{ marginBottom: 16 }}>
        {row.map((s, index2) => (
          <Col span={6} key={`${index}-${index2}`}>
            <Statistic title={renderTitle(s.title, s.explanation)} value={Math.round(s.value ?? 0)} />
          </Col>
        ))}
      </Row>
    ));
  };

  const renderStatisticsBottom = () => {
    const definition = {
      topUsers: {
        title: 'Najaktivniji korisnici',
        explanation: 'Korisnici koji su razmijenili najviše poruka u komunikaciji s AI chatom u odabranom razdoblju.',
      },
      usersOverLimit: {
        title: 'Korisnici koji su dosegli limit',
        explanation:
          'Korisnici koji su dosegli dozvoljeni broj poruka prema trenutno važećem ograničenju broja poruka. Izračun se odnosi na početak i kraj mjeseca odabranog početka razdoblja. Napomena: ograničenja su se mogla mijenjati tijekom odabranog razdoblja, ali ovaj prikaz se odnosi isključivo na trenutno važeće ograničenje broja poruka.',
      },
    };
    return (
      <Row gutter={16}>
        <Col span={12}>
          <List
            header={<strong>{renderTitle(definition.topUsers.title, definition.topUsers.explanation)}</strong>}
            bordered
            dataSource={statistics?.topUsers}
            renderItem={(item) => (
              <List.Item>
                <div className="flex-row space-between w100">
                  <div>{item.name}</div>
                  <div>{item.messageCount}</div>
                </div>
              </List.Item>
            )}
          />
        </Col>
        <Col span={12}>
          {statistics?.usersOverLimit ? (
          <List
            header={
              <strong>{renderTitle(definition.usersOverLimit.title, definition.usersOverLimit.explanation)}</strong>
            }
            bordered
            dataSource={statistics?.usersOverLimit ?? []}
            renderItem={(item) => (
              <List.Item>
                <div className="flex-row space-between w100">
                  <div>{item.name}</div>
                  <div>
                    {item.used} / {item.limit ?? '∞'}
                  </div>
                </div>
              </List.Item>
            )}
          />
          ) : null}
        </Col>
      </Row>
    );
  };

  return (
    <div className="ai-chat-settings-page w100-h100">
      <Space style={{ marginBottom: 16 }} wrap={true}>
        <Select
          value={period}
          style={{ minWidth: 250 }}
          onChange={onPeriodChange}
          options={Periods.map((p) => ({ value: p.id?.toString(), label: getPeriodLabel(p.id) }))}
        />
        {period == PeriodEnum.CUSTOM.toString() && (
          <RangePicker locale={localeHR} allowClear={false} format={DisplayDateFormat} onChange={onDateRangeChange} />
        )}
        <Select
          style={{ minWidth: 250 }}
          placeholder="Filtriraj po korisniku"
          allowClear
          showSearch
          onChange={(response) => setSelectedUser(response ? parseInt(response) : null)}
          options={users.map((x) => ({ value: x.id.toString(), label: x.name }))}
          filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </Space>
      <Spin spinning={!statistics}>
        {renderStatisticsTop()}
        <Divider />
        {renderStatisticsBottom()}
      </Spin>
    </div>
  );
}

export default AiChatStatisticsComponent;
